import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Clock,
  Calendar,
  Users,
  CheckCircle,
  Tv,
  Loader2,
  BookOpen,
  Check,
  ChevronsUpDown,
  Search,
  ImageUp,
  Edit,
  Save,
  AlertCircle,
  Settings,
  FileText,
  Code,
  Mail,
  UserPlus,
  Shield,
  Trash2,
} from "lucide-react";
import { Mosque } from "@phosphor-icons/react";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { auth } from "../config/firebase";
import ImageUploadDialog from "../components/TeleView/ImageUploadDialog";
import { checkAdmin, setActiveMasjid, updateMasjid, searchMasjids } from "../services/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { navigateToLastTVView } from "../utils/tvNavigation";
import { useNavigate } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export default function AdminDashboard() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [adminInfo, setAdminInfo] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMasjid, setSelectedMasjid] = useState("");
  const [selectedMasjidData, setSelectedMasjidData] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [stats, setStats] = useState({
    masjids: 0,
    events: 0,
    admins: 0,
  });
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [masjidData, setMasjidData] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});

  // Admin management state
  const [adminsDialogOpen, setAdminsDialogOpen] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminOwners, setAdminOwners] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminRole, setNewAdminRole] = useState("Admin");
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [adminSuccess, setAdminSuccess] = useState(null);

  // Debounce timer ref for search
  const searchTimerRef = useRef(null);

  const isSuperAdmin = adminInfo?.role === "super";
  const managedMasjidId = adminInfo?.activeMasjidId || adminInfo?.masjidId;
  const managedMasjidName =
    masjidData?.name || masjidData?.title || "Selected masjid";
  const managedLocation = [masjidData?.city, masjidData?.state]
    .filter(Boolean)
    .join(", ");

  const statCards = useMemo(() => {
    if (!isSuperAdmin) return [];
    return [
      {
        title: "Masajid",
        value: stats.masjids,
        icon: Mosque,
        gradient: "from-blue-500/20 to-cyan-500/10",
        iconBg: "bg-blue-500/10",
        iconColor: "text-blue-500",
      },
      {
        title: "Events",
        value: stats.events,
        icon: Calendar,
        gradient: "from-emerald-500/20 to-green-500/10",
        iconBg: "bg-emerald-500/10",
        iconColor: "text-emerald-500",
      },
      {
        title: "Admins",
        value: stats.admins,
        icon: Users,
        gradient: "from-purple-500/20 to-pink-500/10",
        iconBg: "bg-purple-500/10",
        iconColor: "text-purple-500",
      },
    ];
  }, [isSuperAdmin, stats]);

  const actionCards = useMemo(
    () => [
      {
        title: "Prayer Times",
        description: "Manage daily schedules",
        icon: Clock,
        href: "/prayer-times",
        gradient: "from-emerald-500/20 to-green-500/10",
        iconBg: "bg-emerald-500/10",
        iconColor: "text-emerald-500",
        requiresMasjid: true,
      },
      {
        title: "Events",
        description: "Create and manage community events",
        icon: Calendar,
        href: "/events",
        gradient: "from-purple-500/20 to-pink-500/10",
        iconBg: "bg-purple-500/10",
        iconColor: "text-purple-500",
        requiresMasjid: true,
      },
      {
        title: "TV Viewer",
        description: "Display live prayer times",
        icon: Tv,
        onClick: () => navigateToLastTVView(navigate),
        gradient: "from-amber-500/20 to-orange-500/10",
        iconBg: "bg-amber-500/10",
        iconColor: "text-amber-500",
        requiresMasjid: true,
      },
      {
        title: "TV Images",
        description: "Manage slideshow assets",
        icon: ImageUp,
        onClick: () => setShowImageDialog(true),
        gradient: "from-blue-500/20 to-cyan-500/10",
        iconBg: "bg-blue-500/10",
        iconColor: "text-blue-500",
        requiresMasjid: true,
      },
      {
        title: "Website Widgets",
        description: "Embed prayer times, calendars, and events on your website",
        icon: Code,
        href: "/widgets",
        gradient: "from-teal-500/20 to-emerald-500/10",
        iconBg: "bg-teal-500/10",
        iconColor: "text-teal-500",
        requiresMasjid: true,
      },
      {
        title: "Monthly PDF",
        description: "Generate a printable monthly prayer times PDF",
        icon: FileText,
        href: "/monthly-times",
        gradient: "from-sky-500/20 to-blue-500/10",
        iconBg: "bg-sky-500/10",
        iconColor: "text-sky-500",
        requiresMasjid: true,
      },
      {
        title: "Admins",
        description: "Manage team members and permissions",
        icon: Users,
        onClick: () => handleShowAdmins(),
        gradient: "from-indigo-500/20 to-violet-500/10",
        iconBg: "bg-indigo-500/10",
        iconColor: "text-indigo-500",
        requiresMasjid: true,
      },
      {
        title: "Approvals",
        description: "Review masjid submissions",
        icon: CheckCircle,
        href: "/admin/approvals",
        gradient: "from-indigo-500/20 to-violet-500/10",
        iconBg: "bg-indigo-500/10",
        iconColor: "text-indigo-500",
        superOnly: true,
      },
      {
        title: "Halal Approvals",
        description: "Manage halal listings",
        icon: BookOpen,
        href: "/admin/halal-approvals",
        gradient: "from-rose-500/20 to-red-500/10",
        iconBg: "bg-rose-500/10",
        iconColor: "text-rose-500",
        superOnly: true,
      },
      {
        title: "Settings",
        description: "Configure account settings",
        icon: Settings,
        href: "/settings",
        gradient: "from-slate-500/20 to-gray-500/10",
        iconBg: "bg-slate-500/10",
        iconColor: "text-slate-500",
        fullWidth: true,
      },
    ],
    [setShowImageDialog]
  );

  const filteredActions = useMemo(
    () =>
      actionCards.filter((action) => !action.superOnly || isSuperAdmin),
    [actionCards, isSuperAdmin]
  );

  const selectionRequired = isSuperAdmin && !managedMasjidId;

  // Debounced search for masjids
  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);

    // Clear previous timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    // If query is too short, clear results
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    // Debounce the search
    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const result = await searchMasjids(query.trim(), 15);
        if (result.ok) {
          setSearchResults(result.masjids);
        }
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  // Load admin info
  useEffect(() => {
    const loadAdminData = async () => {
      if (!user?.email) return;

      try {
        setLoading(true);
        setError(null);

        // Check admin status
        const adminData = await checkAdmin(user.email);
        if (!adminData.isAdmin) {
          setError("You do not have admin privileges");
          return;
        }

        setAdminInfo(adminData);

        // Load masjid data via API
        const masjidId = adminData.role === "super" ? adminData.activeMasjidId : adminData.masjidId;
        if (masjidId) {
          const token = await auth.currentUser.getIdToken(true);
          const response = await fetch(`${API_URL}/v1/masjid/${masjidId}/details`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const masjidResult = await response.json();
          if (masjidResult.ok) {
            const data = masjidResult.masjid;
            setMasjidData(data);
            setEditData({
              name: data.name || "",
              nickname: data.nickname || "",
              address: data.address || "",
              city: data.city || "",
              state: data.state || "",
              zip: data.zip || "",
              website: data.website || "",
            });
          }
        }

        // Set selected masjid if activeMasjidId exists for super admin
        if (adminData.role === "super" && adminData.activeMasjidId) {
          setSelectedMasjid(adminData.activeMasjidId);
        }

        // Load stats
        await loadStats(adminData);
      } catch (err) {
        console.error("Error loading admin data:", err);
        setError("Failed to load admin data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadAdminData();
  }, [user]);

  // Load statistics
  const loadStats = async (adminData) => {
    try {
      // Only super admins can see stats
      if (adminData.role === "super") {
        const token = await auth.currentUser.getIdToken(true);
        const response = await fetch(`${API_URL}/v1/admin/stats`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.ok) {
          setStats(result.stats);
        }
      } else {
        // For regular admins, set default stats
        setStats({
          masjids: 0,
          events: 0,
          admins: 0,
        });
      }
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  // Handle masjid selection for super admin
  const handleMasjidSelection = async () => {
    if (!selectedMasjid) return;

    try {
      setError(null);
      setSuccess(null);
      await setActiveMasjid(selectedMasjid);
      setSuccess(
        "Masjid selected successfully! You can now manage this masjid."
      );

      // Refresh admin info
      const adminData = await checkAdmin(user.email);
      setAdminInfo(adminData);

      // Load masjid data for newly selected masjid via API
      if (selectedMasjid) {
        const token = await auth.currentUser.getIdToken(true);
        const response = await fetch(`${API_URL}/v1/masjid/${selectedMasjid}/details`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.ok) {
          const data = result.masjid;
          setMasjidData(data);
          setEditData({
            name: data.name || "",
            nickname: data.nickname || "",
            address: data.address || "",
            city: data.city || "",
            state: data.state || "",
            zip: data.zip || "",
            website: data.website || "",
          });
        }
      }
    } catch (err) {
      console.error("Error setting active masjid:", err);
      setError("Failed to select masjid. Please try again.");
    }
  };

  // Handle saving masjid edits
  const handleSaveEdit = async () => {
    if (!masjidData?.id) return;

    try {
      setError(null);
      setSuccess(null);

      // Prepare updates
      const updates = {};
      Object.keys(editData).forEach((key) => {
        if (editData[key] !== masjidData[key]) {
          updates[key] = editData[key];
        }
      });

      if (Object.keys(updates).length === 0) {
        setError("No changes to save");
        setTimeout(() => setError(null), 3000);
        return;
      }

      await updateMasjid(masjidData.id, updates);

      setSuccess("Masjid information updated successfully!");
      setEditDialogOpen(false);

      // Reload masjid data via API
      const token = await auth.currentUser.getIdToken(true);
      const response = await fetch(`${API_URL}/v1/masjid/${masjidData.id}/details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (result.ok) {
        const data = result.masjid;
        setMasjidData(data);
        setEditData({
          name: data.name || "",
          nickname: data.nickname || "",
          address: data.address || "",
          city: data.city || "",
          state: data.state || "",
          zip: data.zip || "",
          website: data.website || "",
        });
      }
    } catch (err) {
      console.error("Error saving masjid info:", err);
      setError(
        err.response?.data?.error || "Failed to save changes. Please try again."
      );
    }
  };

  // Admin management functions
  const loadAdmins = async () => {
    const masjidId = managedMasjidId;
    if (!masjidId || !user) return;

    setAdminsLoading(true);
    setAdminError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(
        `${API_URL}/v1/masjid/${masjidId}/admins`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load admins");
      }

      const data = await response.json();
      setAdminOwners(Array.isArray(data?.owners) ? data.owners : []);
      setAdmins(Array.isArray(data?.admins) ? data.admins : []);
    } catch (e) {
      console.error("load admins failed:", e);
      setAdminOwners([]);
      setAdmins([]);
      setAdminError("Failed to load admins");
    } finally {
      setAdminsLoading(false);
    }
  };

  const handleShowAdmins = async () => {
    setAdminsDialogOpen(true);
    await loadAdmins();
  };

  const addAdmin = async () => {
    const email = (newAdminEmail || "").trim().toLowerCase();
    if (!email) return;

    const masjidId = managedMasjidId;
    if (!masjidId) return;

    setAdminSaving(true);
    setAdminError(null);
    setAdminSuccess(null);

    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await fetch(
        `${API_URL}/v1/masjid/${masjidId}/admins`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, role: newAdminRole }),
        }
      );

      if (response.status === 409) {
        setAdminError(`${email} is already an admin for this masjid.`);
        return;
      }

      if (response.status === 404) {
        setAdminError(
          `The email "${email}" is not registered. Please ask them to create an account first.`
        );
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to add admin");
      }

      setNewAdminEmail("");
      setNewAdminRole("Admin");
      setAdminSuccess(
        `${email} has been added as ${
          newAdminRole === "Admin Owner" ? "an Admin Owner" : "an Admin"
        }.`
      );
      setTimeout(() => setAdminSuccess(null), 3000);
      await loadAdmins();
    } catch (e) {
      console.error("Add admin error:", e);
      setAdminError(e.message || "Failed to add admin. Please try again.");
    } finally {
      setAdminSaving(false);
    }
  };

  const removeAdmin = async (email) => {
    const masjidId = managedMasjidId;
    if (!masjidId) return;

    setAdminSaving(true);
    setAdminError(null);

    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await fetch(
        `${API_URL}/v1/masjid/${masjidId}/admins/${encodeURIComponent(
          email.toLowerCase()
        )}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to remove admin");
      }

      setAdminSuccess(`${email} has been removed.`);
      setTimeout(() => setAdminSuccess(null), 3000);
      await loadAdmins();
    } catch (e) {
      console.error("Remove admin error:", e);
      setAdminError(e.message || "Failed to remove admin");
    } finally {
      setAdminSaving(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    try {
      const date = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading your dashboard...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Inline alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Stats (super admin only) */}
        {isSuperAdmin && statCards.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {statCards.map((card) => {
              const Icon = card.icon;
              return (
                <Card
                  key={card.title}
                  className={`bg-gradient-to-br ${card.gradient}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-lg ${card.iconBg}`}
                      >
                        <Icon className={`h-6 w-6 ${card.iconColor}`} />
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {card.title}
                      </p>
                      <p className="mt-1 text-3xl font-bold text-foreground">
                        {card.value}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Masjid Context (super admin only) */}
        {isSuperAdmin && (
          <Card>
            <CardHeader>
              <CardTitle>Masjid Context</CardTitle>
              <CardDescription>
                Choose which masjid you want to manage for admin tools.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <Label htmlFor="masjid-select">Select Masjid</Label>
                  <Popover open={open} onOpenChange={(isOpen) => {
                    setOpen(isOpen);
                    if (!isOpen) {
                      setSearchQuery("");
                      setSearchResults([]);
                    }
                  }}>
                    <PopoverTrigger asChild>
                      <Button
                        id="masjid-select"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="mt-2 w-full justify-between"
                      >
                        {selectedMasjid && selectedMasjidData
                          ? (() => {
                              const name = selectedMasjidData?.nickname || selectedMasjidData?.name || selectedMasjidData?.title || "Selected masjid";
                              const city = selectedMasjidData?.city;
                              const state = selectedMasjidData?.state;
                              if (city && state) return `${name} - ${city}, ${state}`;
                              if (city) return `${name} - ${city}`;
                              return name;
                            })()
                          : "Search for a masjid..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[var(--radix-popover-trigger-width)] p-0"
                      align="start"
                      side="bottom"
                      sideOffset={5}
                    >
                      <div className="flex flex-col">
                        <div className="flex items-center border-b px-3">
                          {searching ? (
                            <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
                          ) : (
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                          )}
                          <Input
                            placeholder="Type to search masjids..."
                            className="flex h-11 w-full border-0 bg-transparent py-3 text-sm outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
                            value={searchQuery}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto p-1">
                          {searchQuery.trim().length < 2 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              Type at least 2 characters to search
                            </div>
                          ) : searching ? (
                            <div className="flex items-center justify-center py-6">
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                          ) : searchResults.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              No masjid found.
                            </div>
                          ) : (
                            searchResults.map((masjid) => (
                              <div
                                key={masjid.id}
                                className={cn(
                                  "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                                  selectedMasjid === masjid.id && "bg-accent"
                                )}
                                onClick={() => {
                                  setSelectedMasjid(masjid.id);
                                  setSelectedMasjidData(masjid);
                                  setSearchQuery("");
                                  setSearchResults([]);
                                  setOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedMasjid === masjid.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col flex-1">
                                  <span>{masjid.nickname || masjid.name || "Unnamed Masjid"}</span>
                                  {(masjid.nickname && masjid.name) && (
                                    <span className="text-xs text-muted-foreground">
                                      {masjid.name}
                                    </span>
                                  )}
                                  {(masjid.city || masjid.state) && (
                                    <span className="text-xs text-muted-foreground">
                                      {masjid.city}
                                      {masjid.city && masjid.state ? ", " : ""}
                                      {masjid.state}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Button
                  onClick={handleMasjidSelection}
                  disabled={!selectedMasjid}
                  className="w-full sm:w-auto"
                >
                  Set Active Masjid
                </Button>
              </div>

              {adminInfo?.activeMasjidId && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Currently managing: <strong>{managedMasjidName}</strong>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Selection required */}
        {selectionRequired && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Select a masjid above to unlock admin tools.
            </AlertDescription>
          </Alert>
        )}

        {/* Masjid Info */}
        {masjidData && managedMasjidId && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Masjid Information</CardTitle>
                <CardDescription>
                  {managedLocation || "Managed masjid details"}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditDialogOpen(true)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Masjid Name
                  </p>
                  <p className="mt-1 text-base font-medium text-foreground">
                    {masjidData.name}
                  </p>
                </div>
                {masjidData.nickname && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Nickname
                    </p>
                    <p className="mt-1 text-base font-medium text-foreground">
                      {masjidData.nickname}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Address
                  </p>
                  <p className="mt-1 text-base font-medium text-foreground">
                    {masjidData.address}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    City &amp; State
                  </p>
                  <p className="mt-1 text-base font-medium text-foreground">
                    {`${masjidData.city || ""}, ${masjidData.state || ""} ${masjidData.zip || ""}`.trim()}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Website
                  </p>
                  <p className="mt-1 text-base font-medium text-foreground">
                    {masjidData.website || "Not set"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin Tools */}
        <div>
          <div className="mb-4">
            <h2 className="text-2xl font-semibold tracking-tight">Admin Tools</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Prayer times, events, TV displays, embeddable widgets, monthly PDFs, and team management.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredActions.map((action) => {
              const Icon = action.icon;
              const isDisabled =
                (action.requiresMasjid && !managedMasjidId) ||
                (action.superOnly && !isSuperAdmin);

              const handleAction = () => {
                if (isDisabled) return;
                if (action.onClick) {
                  action.onClick();
                  return;
                }
                if (action.href) {
                  window.location.assign(action.href);
                }
              };

              return (
                <Card
                  key={action.title}
                  onClick={handleAction}
                  tabIndex={0}
                  role="button"
                  aria-disabled={isDisabled}
                  onKeyDown={(e) => {
                    if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      handleAction();
                    }
                  }}
                  className={`bg-gradient-to-br ${action.gradient} transition ${
                    isDisabled
                      ? "opacity-60 cursor-not-allowed"
                      : "cursor-pointer hover:-translate-y-1 hover:shadow-lg"
                  } ${action.fullWidth ? "sm:col-span-2 lg:col-span-3" : ""}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${action.iconBg}`}
                      >
                        <Icon className={`h-6 w-6 ${action.iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-semibold text-foreground">
                          {action.title}
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {isDisabled && action.requiresMasjid
                            ? "Select a masjid first"
                            : action.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>Your details and permissions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Your Role
                </p>
                <div className="mt-2">
                  <Badge variant="secondary">{adminInfo?.role || "N/A"}</Badge>
                </div>
              </div>
              {managedMasjidId && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Masjid
                  </p>
                  <p className="mt-1 text-base font-medium text-foreground">
                    Configured
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Email
                </p>
                <p className="mt-1 text-base font-medium text-foreground">
                  {user?.email}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Masjid Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>Edit Masjid Information</DialogTitle>
            <DialogDescription>
              Update your masjid's information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-2">
            <div className="space-y-2">
              <Label htmlFor="name">Masjid Name</Label>
              <Input
                id="name"
                value={editData.name || ""}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nickname">Nickname (Optional)</Label>
              <Input
                id="nickname"
                value={editData.nickname || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 15) {
                    setEditData({ ...editData, nickname: value });
                  }
                }}
                placeholder="Short name (max 15 characters)"
                maxLength={15}
              />
              {editData.nickname && (
                <p className="text-xs text-muted-foreground">
                  {editData.nickname.length}/15 characters
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={editData.address || ""}
                onChange={(e) =>
                  setEditData({ ...editData, address: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={editData.city || ""}
                onChange={(e) =>
                  setEditData({ ...editData, city: e.target.value })
                }
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={editData.state || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, state: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  value={editData.zip || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, zip: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={editData.website || ""}
                onChange={(e) =>
                  setEditData({ ...editData, website: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter className="shrink-0 pt-4">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Management Dialog */}
      <Dialog open={adminsDialogOpen} onOpenChange={setAdminsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle>Manage Admins</DialogTitle>
                {managedMasjidName && (
                  <DialogDescription className="mt-0.5">
                    {managedMasjidName}
                  </DialogDescription>
                )}
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-4 overflow-hidden flex-1">
            {/* Add Admin Section */}
            <div className="rounded-lg bg-muted/50 p-4 space-y-3 shrink-0">
              <Label className="text-sm font-medium">Add Admin</Label>

              {/* Role Selector - Super Admins can assign any role */}
              {isSuperAdmin && (
                <div className="flex gap-2">
                  <Button
                    variant={newAdminRole === "Admin" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setNewAdminRole("Admin")}
                    className="flex-1"
                  >
                    {newAdminRole === "Admin" && (
                      <Check className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Regular Admin
                  </Button>
                  <Button
                    variant={
                      newAdminRole === "Admin Owner" ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => setNewAdminRole("Admin Owner")}
                    className="flex-1"
                  >
                    {newAdminRole === "Admin Owner" && (
                      <Check className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Admin Owner
                  </Button>
                </div>
              )}

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addAdmin()}
                    className="pl-9"
                    disabled={adminSaving}
                  />
                </div>
                <Button
                  onClick={addAdmin}
                  disabled={adminSaving || !newAdminEmail.trim()}
                  className="gap-1.5"
                >
                  {adminSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                  Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Grant admin access to manage this masjid
              </p>
            </div>

            {/* Error/Success Messages */}
            {adminError && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive shrink-0">
                {adminError}
              </div>
            )}
            {adminSuccess && (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 text-sm text-emerald-600 dark:text-emerald-400 shrink-0">
                {adminSuccess}
              </div>
            )}

            {/* Admin List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {adminsLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Loading admins...
                  </p>
                </div>
              ) : adminOwners.length === 0 && admins.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="font-medium">No admins yet</p>
                  <p className="text-sm text-muted-foreground text-center">
                    Add admins to help manage this masjid
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Admin Owners Section */}
                  {adminOwners.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Admin Owners
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      <div className="space-y-2">
                        {adminOwners.map(({ email, createdAt }) => (
                          <div
                            key={email}
                            className="flex items-center gap-3 rounded-lg border bg-card p-3"
                          >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                              <Shield className="h-4 w-4 text-amber-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-medium">
                                  {email}
                                </p>
                                {email === user?.email && (
                                  <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                    You
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Owner • Added {formatTimestamp(createdAt)}
                              </p>
                            </div>
                            {isSuperAdmin && email !== user?.email && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Remove ${email} as an Admin Owner?`
                                    )
                                  ) {
                                    removeAdmin(email);
                                  }
                                }}
                                disabled={adminSaving}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Regular Admins Section */}
                  {admins.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Admins
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      <div className="space-y-2">
                        {admins.map(({ email, createdAt }) => (
                          <div
                            key={email}
                            className="flex items-center gap-3 rounded-lg border bg-card p-3"
                          >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                              <Users className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="truncate text-sm font-medium">
                                {email}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Added {formatTimestamp(createdAt)}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0 h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => {
                                if (
                                  window.confirm(`Remove ${email} as an Admin?`)
                                ) {
                                  removeAdmin(email);
                                }
                              }}
                              disabled={adminSaving}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="shrink-0 pt-4">
            <Button
              variant="outline"
              onClick={() => setAdminsDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageUploadDialog
        masjidId={adminInfo?.masjidId || adminInfo?.activeMasjidId}
        open={showImageDialog}
        onOpenChange={setShowImageDialog}
      />
    </AppLayout>
  );
}
